import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { Action, PhraseRef, PlayerView, PuzzleAct, Role } from '@il-colpo/engine';
import { audio } from '../audio/manager.ts';
import { sfxForAct } from '../audio/sfx.ts';
import { useToastStore } from '../state/toastStore.ts';
import type { GameClient } from './client.ts';
import { useLooseT, type LooseT } from './text/loose.ts';
import { phraseText } from './text/phraseText.ts';

export interface GameCtx {
  client: GameClient;
  view: PlayerView;
  role: Role;
  /** Sends an action; a refusal is shown as a short message and returned as `false`. */
  send: (action: Action) => boolean;
  act: (puzzleId: string, act: PuzzleAct) => boolean;
  say: (phrase: PhraseRef) => boolean;
  /** The sentence a phrase says, in the current language. */
  sentence: (phrase: PhraseRef) => string;
  /** Loose lookups into the `game` namespace (`g('roles.hacker')`). */
  g: LooseT;
}

const Ctx = createContext<GameCtx | null>(null);

export function GameProvider({
  client,
  view,
  asRole,
  children,
}: {
  client: GameClient;
  view: PlayerView;
  /** Training: show and act as another seat (the cards of the teammates). */
  asRole?: Role;
  children: ReactNode;
}) {
  const g = useLooseT('game');
  const p = useLooseT('phrases');
  const { t } = useTranslation('game');
  const push = useToastStore((s) => s.push);

  const send = useCallback(
    (action: Action): boolean => {
      const r = asRole && client.sendAs ? client.sendAs(asRole, action) : client.send(action);
      if (!r.ok) {
        const known = `errors.${r.error.code}`;
        const text = g(known);
        push(text === known ? t('errors.generic') : text, 'danger');
      }
      return r.ok;
    },
    [client, asRole, g, push, t],
  );

  const value = useMemo<GameCtx>(
    () => ({
      client,
      view,
      role: view.you.role,
      send,
      act: (puzzleId, act) => {
        const ok = send({ type: 'puzzle.act', puzzleId, act });
        // The sound of the player's hands, now; the verdict (error, solved) follows as an event.
        if (ok) audio.sfx(sfxForAct(act));
        return ok;
      },
      say: (phrase) => send({ type: 'chat.quick', phrase }),
      sentence: (phrase) => phraseText({ t: p, players: view.players }, phrase),
      g,
    }),
    [client, view, send, g, p],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useGame(): GameCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useGame outside a game');
  return ctx;
}
